import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

interface ComplianceReport {
  reportDate: string;
  userCounts: {
    total: number;
    minors: number;
    adults: number;
    withConsent: number;
    withoutConsent: number;
  };
  dataRetention: {
    expiredData: number;
    retentionPeriods: { [key: string]: number };
  };
  deletionRequests: {
    pending: number;
    processed: number;
    scheduled: number;
  };
  internationalCompliance: {
    countryCounts: { [key: string]: number };
  };
  recommendations: Array<{
    priority: string;
    category: string;
    message: string;
    action: string;
  }>;
}

const AdminReports: React.FC = () => {
  const [report, setReport] = useState<ComplianceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchComplianceReport();
  }, []);

  const fetchComplianceReport = async () => {
    try {
      const response = await apiClient.get('/v1/admin/compliance/report');
      setReport(response.data);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    if (!report) return;

    const reportData = {
      ...report,
      generatedBy: 'Admin Panel',
      generatedAt: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-report-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadCSV = () => {
    if (!report) return;

    const csvData = [
      ['Metric', 'Value'],
      ['Report Date', report.reportDate],
      ['Total Users', report.userCounts.total.toString()],
      ['Adult Users', report.userCounts.adults.toString()],
      ['Minor Users', report.userCounts.minors.toString()],
      ['Users with Consent', report.userCounts.withConsent.toString()],
      ['Users without Consent', report.userCounts.withoutConsent.toString()],
      ['Expired Data Users', report.dataRetention.expiredData.toString()],
      ['Pending Deletions', report.deletionRequests.pending.toString()],
      ['Processed Deletions', report.deletionRequests.processed.toString()],
      ['Scheduled Deletions', report.deletionRequests.scheduled.toString()],
      ...Object.entries(report.dataRetention.retentionPeriods).map(([period, count]) => [
        `Retention Period: ${period}`, count.toString()
      ]),
      ...Object.entries(report.internationalCompliance.countryCounts).map(([country, count]) => [
        `Country: ${country}`, count.toString()
      ]),
    ];

    const csvContent = csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getComplianceScore = () => {
    if (!report) return 0;
    
    const totalUsers = report.userCounts.total;
    if (totalUsers === 0) return 100;
    
    const usersWithConsent = report.userCounts.withConsent;
    const expiredData = report.dataRetention.expiredData;
    const pendingDeletions = report.deletionRequests.pending;
    
    // Calculate compliance score (0-100)
    const consentScore = (usersWithConsent / totalUsers) * 40; // 40% weight
    const dataRetentionScore = ((totalUsers - expiredData) / totalUsers) * 30; // 30% weight
    const deletionScore = ((totalUsers - pendingDeletions) / totalUsers) * 30; // 30% weight
    
    return Math.round(consentScore + dataRetentionScore + deletionScore);
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <h1 className="admin-page-title">Compliance Reports</h1>
          <p className="admin-page-description">Loading compliance reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <h1 className="admin-page-title">Compliance Reports</h1>
          <p className="admin-page-description">Error loading compliance reports</p>
        </div>
        <div className="admin-alert danger">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (!report) return null;

  const complianceScore = getComplianceScore();

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Compliance Reports</h1>
        <p className="admin-page-description">
          Generate and download compliance reports for auditing and legal requirements
        </p>
      </div>

      {/* Report Actions */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Report Actions</h2>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button className="admin-button" onClick={downloadReport}>
            📄 Download JSON Report
          </button>
          <button className="admin-button" onClick={downloadCSV}>
            📊 Download CSV Report
          </button>
          <button className="admin-button secondary" onClick={fetchComplianceReport}>
            🔄 Refresh Data
          </button>
        </div>
      </div>

      {/* Compliance Score */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Overall Compliance Score</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ 
            fontSize: '4rem', 
            fontWeight: 'bold', 
            color: complianceScore >= 90 ? '#2e7d32' : complianceScore >= 70 ? '#f57c00' : '#d32f2f',
            marginBottom: '1rem'
          }}>
            {complianceScore}%
          </div>
          <div style={{ fontSize: '1.2rem', color: '#666' }}>
            {complianceScore >= 90 ? '✅ Excellent Compliance' : 
             complianceScore >= 70 ? '⚠️ Good Compliance' : 
             '❌ Needs Improvement'}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#999', marginTop: '0.5rem' }}>
            Report generated on {formatDate(report.reportDate)}
          </div>
        </div>
      </div>

      {/* Key Metrics Summary */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Key Metrics Summary</h2>
        </div>
        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <div className="admin-stat-value">{report.userCounts.total}</div>
            <div className="admin-stat-label">Total Users</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{Math.round((report.userCounts.withConsent / report.userCounts.total) * 100)}%</div>
            <div className="admin-stat-label">Consent Rate</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{report.userCounts.minors}</div>
            <div className="admin-stat-label">Minor Users</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{report.dataRetention.expiredData}</div>
            <div className="admin-stat-label">Expired Data</div>
          </div>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Detailed Breakdown</h2>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
          {/* User Demographics */}
          <div>
            <h4>👥 User Demographics</h4>
            <table style={{ width: '100%', fontSize: '0.9rem' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>Total Users:</td>
                  <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                    <strong>{report.userCounts.total}</strong>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>Adults (18+):</td>
                  <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                    <strong>{report.userCounts.adults}</strong>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>Minors (&lt;18):</td>
                  <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                    <strong>{report.userCounts.minors}</strong>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '0.5rem 0' }}>Minor Percentage:</td>
                  <td style={{ padding: '0.5rem 0', textAlign: 'right' }}>
                    <strong>{Math.round((report.userCounts.minors / report.userCounts.total) * 100)}%</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* GDPR Compliance */}
          <div>
            <h4>🛡️ GDPR Compliance</h4>
            <table style={{ width: '100%', fontSize: '0.9rem' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>With Consent:</td>
                  <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                    <strong>{report.userCounts.withConsent}</strong>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>Without Consent:</td>
                  <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                    <strong>{report.userCounts.withoutConsent}</strong>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '0.5rem 0' }}>Consent Rate:</td>
                  <td style={{ padding: '0.5rem 0', textAlign: 'right' }}>
                    <strong>{Math.round((report.userCounts.withConsent / report.userCounts.total) * 100)}%</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Data Retention */}
          <div>
            <h4>🗂️ Data Retention</h4>
            <table style={{ width: '100%', fontSize: '0.9rem' }}>
              <tbody>
                {Object.entries(report.dataRetention.retentionPeriods).map(([period, count]) => (
                  <tr key={period}>
                    <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>{period}:</td>
                    <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                      <strong>{count}</strong>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td style={{ padding: '0.5rem 0' }}>Expired Data:</td>
                  <td style={{ padding: '0.5rem 0', textAlign: 'right' }}>
                    <strong style={{ color: report.dataRetention.expiredData > 0 ? '#d32f2f' : '#2e7d32' }}>
                      {report.dataRetention.expiredData}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* International Compliance */}
          <div>
            <h4>🌍 International Distribution</h4>
            <table style={{ width: '100%', fontSize: '0.9rem' }}>
              <tbody>
                {Object.entries(report.internationalCompliance.countryCounts).map(([country, count]) => (
                  <tr key={country}>
                    <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>{country}:</td>
                    <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                      <strong>{count}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {report.recommendations.length > 0 && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">⚠️ Recommendations</h2>
          </div>
          {report.recommendations.map((rec, index) => (
            <div key={index} className={`admin-alert ${
              rec.priority === 'high' ? 'danger' : 
              rec.priority === 'medium' ? 'warning' : 'info'
            }`}>
              <strong>{rec.category}:</strong> {rec.message}
              <br />
              <em>Recommended Action: {rec.action}</em>
            </div>
          ))}
        </div>
      )}

      {/* Legal Disclaimer */}
      <div className="admin-alert info">
        <strong>Legal Disclaimer:</strong> This report is generated for internal compliance monitoring purposes. 
        Ensure all data handling practices comply with applicable laws and regulations in your jurisdiction. 
        Consult with legal counsel for specific compliance requirements.
      </div>
    </div>
  );
};

export default AdminReports;